import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import { ArrowLeft, Users, Shield, UserCheck, Building2, UsersRound, Save, TrendingUp, Calendar, Activity } from "lucide-react";
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import ModuleSettings from "@/components/admin/ModuleSettings";

export default function AdminDashboard() {
  const { adminId } = useParams<{ adminId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isSuperAdmin } = useUserRoles();
  const queryClient = useQueryClient();

  const [limits, setLimits] = useState({
    max_users: "",
    max_teams: "",
    max_managers: "",
    max_clients: "",
    max_guards: "",
  });

  // Fetch admin profile
  const { data: adminProfile } = useQuery({
    queryKey: ["adminProfile", adminId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", adminId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!adminId,
  });

  // Fetch admin usage statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["adminStats", adminId],
    queryFn: async () => {
      // Get all admin IDs in the contributor group
      const { data: groupData, error: groupError } = await supabase.rpc('get_admin_group', { user_id: adminId });
      if (groupError) throw groupError;
      
      const adminGroupIds = groupData?.map((row: any) => row.admin_id) || [adminId];

      // Get all users created by any admin in the group
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select("id, role, created_at")
        .in("created_by", adminGroupIds);
      if (usersError) throw usersError;

      // Get teams created by any admin in the group
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select("id, name, created_at")
        .in("created_by", adminGroupIds);
      if (teamsError) throw teamsError;

      // Get user roles created by admins in the group
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("role, user_id")
        .in("user_id", usersData?.map(u => u.id) || []);
      if (rolesError) throw rolesError;

      const managers = rolesData?.filter(r => r.role === "manager").length || 0;
      const clients = rolesData?.filter(r => r.role === "client").length || 0;
      const guards = rolesData?.filter(r => r.role === "guard").length || 0;

      // Get candidates created by admins in the group
      const { data: candidatesData, error: candidatesError } = await supabase
        .from("candidates")
        .select("id")
        .in("created_by", adminGroupIds);
      if (candidatesError) throw candidatesError;

      // Get missions created by admins in the group
      const { data: missionsData, error: missionsError } = await supabase
        .from("missions")
        .select("id")
        .in("created_by", adminGroupIds);
      if (missionsError) throw missionsError;

      return {
        totalUsers: usersData?.length || 0,
        totalTeams: teamsData?.length || 0,
        totalManagers: managers,
        totalClients: clients,
        totalGuards: guards,
        totalCandidates: candidatesData?.length || 0,
        totalMissions: missionsData?.length || 0,
        recentUsers: usersData?.slice(0, 5) || [],
        recentTeams: teamsData?.slice(0, 5) || [],
        contributorGroupSize: adminGroupIds.length,
      };
    },
    enabled: !!adminId,
  });

  // Fetch current admin limits
  const { data: currentLimits } = useQuery({
    queryKey: ["adminLimits", adminId],
    queryFn: async () => {
      // Get parent admin ID (if this admin is a contributor)
      const { data: parentData } = await supabase.rpc('get_parent_admin', { user_id: adminId });
      const parentAdminId = parentData || adminId;

      const { data, error } = await supabase
        .from("admin_limits")
        .select("*")
        .eq("admin_id", parentAdminId)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!adminId && isSuperAdmin,
  });

  useEffect(() => {
    if (currentLimits) {
      setLimits({
        max_users: currentLimits.max_users?.toString() || "",
        max_teams: currentLimits.max_teams?.toString() || "",
        max_managers: currentLimits.max_managers?.toString() || "",
        max_clients: currentLimits.max_clients?.toString() || "",
        max_guards: currentLimits.max_guards?.toString() || "",
      });
    }
  }, [currentLimits]);

  // Mutation to save admin limits
  const saveLimitsMutation = useMutation({
    mutationFn: async () => {
      // Get parent admin ID (if this admin is a contributor)
      const { data: parentData } = await supabase.rpc('get_parent_admin', { user_id: adminId });
      const parentAdminId = parentData || adminId;

      const limitsData = {
        admin_id: parentAdminId,
        max_users: limits.max_users ? parseInt(limits.max_users) : null,
        max_teams: limits.max_teams ? parseInt(limits.max_teams) : null,
        max_managers: limits.max_managers ? parseInt(limits.max_managers) : null,
        max_clients: limits.max_clients ? parseInt(limits.max_clients) : null,
        max_guards: limits.max_guards ? parseInt(limits.max_guards) : null,
      };

      const { error } = await supabase
        .from("admin_limits")
        .upsert(limitsData, { onConflict: "admin_id" });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Limits saved",
        description: "Admin account limits have been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["adminLimits", adminId] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (!isSuperAdmin) {
    return (
      <AppLayout>
        <div className="container py-8">
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">You don't have permission to view this page.</p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const usageData = [
    { label: "Total Users", value: stats?.totalUsers || 0, limit: currentLimits?.max_users, icon: Users, color: "text-blue-500" },
    { label: "Teams", value: stats?.totalTeams || 0, limit: currentLimits?.max_teams, icon: Building2, color: "text-green-500" },
    { label: "Managers", value: stats?.totalManagers || 0, limit: currentLimits?.max_managers, icon: UserCheck, color: "text-purple-500" },
    { label: "Clients", value: stats?.totalClients || 0, limit: currentLimits?.max_clients, icon: UsersRound, color: "text-orange-500" },
    { label: "Guards", value: stats?.totalGuards || 0, limit: currentLimits?.max_guards, icon: Shield, color: "text-red-500" },
  ];

  return (
    <AppLayout>
      <div className="container py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate("/settings")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{adminProfile?.full_name || "Admin Dashboard"}</h1>
              <p className="text-muted-foreground">{adminProfile?.email}</p>
              {stats?.contributorGroupSize && stats.contributorGroupSize > 1 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Contributor Group: {stats.contributorGroupSize} admin{stats.contributorGroupSize > 1 ? 's' : ''} sharing limits
                </p>
              )}
            </div>
          </div>
          <Badge variant="default" className="gap-2">
            <Shield className="h-4 w-4" />
            Administrator
          </Badge>
        </div>

        {/* Usage Statistics Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {usageData.map((item) => (
            <Card key={item.label}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{item.label}</CardTitle>
                <item.icon className={`h-4 w-4 ${item.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{item.value}</div>
                {item.limit && (
                  <p className="text-xs text-muted-foreground">
                    Limit: {item.limit} {item.value >= item.limit && <span className="text-destructive">(Reached)</span>}
                  </p>
                )}
                {!item.limit && (
                  <p className="text-xs text-muted-foreground">No limit set</p>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Additional Stats */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Candidates</CardTitle>
              <Activity className="h-4 w-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalCandidates || 0}</div>
              <p className="text-xs text-muted-foreground">Total candidates</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Missions</CardTitle>
              <TrendingUp className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalMissions || 0}</div>
              <p className="text-xs text-muted-foreground">Total missions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Account Age</CardTitle>
              <Calendar className="h-4 w-4 text-pink-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {adminProfile?.created_at 
                  ? Math.floor((new Date().getTime() - new Date(adminProfile.created_at).getTime()) / (1000 * 60 * 60 * 24))
                  : 0}d
              </div>
              <p className="text-xs text-muted-foreground">Days since creation</p>
            </CardContent>
          </Card>
        </div>

        {/* Module Settings */}
        <ModuleSettings adminId={adminId!} />

        {/* Account Limits Management */}
        <Card>
          <CardHeader>
            <CardTitle>Account Limits</CardTitle>
            <CardDescription>
              Set usage limits for this admin account{stats?.contributorGroupSize && stats.contributorGroupSize > 1 ? ' and their contributor group' : ''}. Leave blank for unlimited.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="max_users">Max Total Users</Label>
                <Input
                  id="max_users"
                  type="number"
                  placeholder="Unlimited"
                  value={limits.max_users}
                  onChange={(e) => setLimits({ ...limits, max_users: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_teams">Max Teams</Label>
                <Input
                  id="max_teams"
                  type="number"
                  placeholder="Unlimited"
                  value={limits.max_teams}
                  onChange={(e) => setLimits({ ...limits, max_teams: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_managers">Max Managers</Label>
                <Input
                  id="max_managers"
                  type="number"
                  placeholder="Unlimited"
                  value={limits.max_managers}
                  onChange={(e) => setLimits({ ...limits, max_managers: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_clients">Max Clients</Label>
                <Input
                  id="max_clients"
                  type="number"
                  placeholder="Unlimited"
                  value={limits.max_clients}
                  onChange={(e) => setLimits({ ...limits, max_clients: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_guards">Max Guards</Label>
                <Input
                  id="max_guards"
                  type="number"
                  placeholder="Unlimited"
                  value={limits.max_guards}
                  onChange={(e) => setLimits({ ...limits, max_guards: e.target.value })}
                />
              </div>
            </div>
            <Separator />
            <div className="flex justify-end">
              <Button
                onClick={() => saveLimitsMutation.mutate()}
                disabled={saveLimitsMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {saveLimitsMutation.isPending ? "Saving..." : "Save Limits"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
